import { useCallback } from "react";
import { useNavigate } from "react-router";

import { PolicyCustomization } from "@covertree/new-business-policy-customization";
import {
  GetPricedCustomizedPolicyQueryVariables,
  SaveCustomizedPolicyMutation,
  UnitLevelCoverages,
} from "@covertree/new-business-graphql";
import {
  removeRevisionDependentCoveragesFromCustomizationOptions,
  saveCustomizedPolicy,
} from "@covertree/new-business-redux";

import { client } from "@apolloClient/client";
import { useAppDispatch } from "@redux/hooks";
import { featureFlags } from "@utils/featureFlags";

export function PolicyCustomizationStep() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const goBack = useCallback(
    (shouldClearCache: boolean) => {
      navigate(-1);

      // If the effective date changes - make sure to clear the cache
      // as if someone moves back and forth between revisions
      // they may have different UW questions, different discounts and
      // different extra coverages prices
      if (shouldClearCache) {
        setTimeout(() => {
          client.cache.evict({
            id: "ROOT_QUERY",
            fieldName: "getPricedCustomizedPolicy",
          });
          client.cache.evict({
            id: "ROOT_QUERY",
            fieldName: "getExtraCoveragePrices",
          });
        }, 100);
      }
    },
    [navigate]
  );

  const onSaveCustomizedPolicy = useCallback(
    async (
      payload: Omit<GetPricedCustomizedPolicyQueryVariables, "policyLocator">
    ): Promise<SaveCustomizedPolicyMutation["saveCustomizedPolicy"]> => {
      return await dispatch(saveCustomizedPolicy(payload)).unwrap();
    },
    [dispatch]
  );

  const removeRevisionDependentCoverages = useCallback(
    (freshUnitLevelCoverages: UnitLevelCoverages) => {
      dispatch(
        removeRevisionDependentCoveragesFromCustomizationOptions({
          freshUnitLevelCoverages,
        })
      );
    },
    [dispatch]
  );

  return (
    <PolicyCustomization
      goBackAndClearCache={goBack}
      useNewOffersAndSelectedQuotePricing={featureFlags.useNewOffersAndSelectedQuotePricing.isEnabled()}
      onSaveCustomizedPolicy={onSaveCustomizedPolicy}
      onRemoveRevisionDependentCoverages={removeRevisionDependentCoverages}
    />
  );
}
